/**
 * Local file reading for the diff panels. Files are read with the File API
 * and never leave the browser.
 */

/** 50 MB: 25x DiffNow's free limit and 6x its premium limit. */
export const DEFAULT_MAX_FILE_BYTES = 50 * 1024 * 1024;

/** Bytes inspected for encoding/binary sniffing (same window git uses). */
const SNIFF_BYTES = 8000;

export type TextEncodingName = 'utf-8' | 'utf-16le' | 'utf-16be';

export interface LoadedTextFile {
  name: string;
  size: number;
  text: string;
  encoding: TextEncodingName;
}

export type FileLoadErrorCode = 'too-large' | 'binary' | 'read-failed';

export class FileLoadError extends Error {
  constructor(public readonly code: FileLoadErrorCode, message: string) {
    super(message);
    this.name = 'FileLoadError';
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Detect a byte-order mark; defaults to UTF-8 without BOM. */
export function sniffEncoding(bytes: Uint8Array): { encoding: TextEncodingName; bomLength: number } {
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return { encoding: 'utf-8', bomLength: 3 };
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return { encoding: 'utf-16le', bomLength: 2 };
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return { encoding: 'utf-16be', bomLength: 2 };
  return { encoding: 'utf-8', bomLength: 0 };
}

/** A NUL byte in the sniff window of a UTF-8 file means binary (git's heuristic). */
export function looksBinary(bytes: Uint8Array, encoding: TextEncodingName): boolean {
  if (encoding !== 'utf-8') return false;
  const end = Math.min(bytes.length, SNIFF_BYTES);
  for (let i = 0; i < end; i++) {
    if (bytes[i] === 0) return true;
  }
  return false;
}

function readBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

export async function readTextFile(
  file: File,
  maxBytes: number = DEFAULT_MAX_FILE_BYTES,
): Promise<LoadedTextFile> {
  if (file.size > maxBytes) {
    throw new FileLoadError(
      'too-large',
      `${file.name} is ${formatBytes(file.size)}; the limit is ${formatBytes(maxBytes)}.`,
    );
  }

  let bytes: Uint8Array;
  try {
    bytes = await readBytes(file);
  } catch {
    throw new FileLoadError('read-failed', `Could not read ${file.name}.`);
  }

  const { text, encoding } = decodeText(bytes, file.name);
  return { name: file.name, size: file.size, text, encoding };
}

/** Shared by files and URL bodies: reject binary content, honour a BOM, decode. */
function decodeText(bytes: Uint8Array, label: string): { text: string; encoding: TextEncodingName } {
  const { encoding, bomLength } = sniffEncoding(bytes);
  if (looksBinary(bytes, encoding)) {
    throw new FileLoadError('binary', `${label} looks like a binary file; only text files can be compared.`);
  }
  return { text: new TextDecoder(encoding).decode(bytes.subarray(bomLength)), encoding };
}

/** Decode a fetched URL body with the same size, binary and BOM rules as local files. */
export async function readResponseText(
  response: Response,
  maxBytes: number = DEFAULT_MAX_FILE_BYTES,
): Promise<string> {
  const tooLarge = (bytes: number) =>
    new FileLoadError('too-large', `That URL returns ${formatBytes(bytes)}; the limit is ${formatBytes(maxBytes)}.`);
  const declared = Number(response.headers.get('content-length') || 0);
  if (declared > maxBytes) throw tooLarge(declared);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.length > maxBytes) throw tooLarge(bytes.length);
  return decodeText(bytes, 'That URL').text;
}
